import { Button, Col, Modal, Row } from 'react-dsgov';
import style from './titularArrendatarioJudicial.module.scss';
import { BrButton, BrDivider, BrInput } from '@govbr-ds/react-components';
import { useEffect, useState } from 'react';
import { useNotificacao } from '../../../../hooks/useNotificacao';
import classNames from 'classnames';
import { IProcessoJuridico } from '../../../../interfaces/IIdentificacaoBasica';
import { useBackEndRequests } from '../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import CustomConfirmer from '../../../../components/customConfirmer/customConfirmer';

interface ITitularArrendatarioJudicialProps {
  declaracaoId: string;
}

const TitularArrendatarioJudicial = ({ declaracaoId }: ITitularArrendatarioJudicialProps) => {
  const KEY_ENTER = 'Enter';

  const COL_NUMERO_PROCESSO = 10;
  const COL_ACOES = 2;

  const MAX_PROCESSO_JURIDICO = 40;

  const [houveDecisaoJudicial, setHouveDecisaoJudicial] = useState(false);
  const [numeroProcesso, setNumeroProcesso] = useState<string>('');
  const [listaProcesso, setListaProcesso] = useState<IProcessoJuridico[]>([]);

  const [isModalConfirmacaoOpen, setIsModalConfirmacaoOpen] = useState(false);

  const mostrarNotificacao = useNotificacao();
  const { getListaProcessoJuridico, postProcessoJuridicio, deleteProcessoJuridicoById, deleteAllProcessosJuridicos } =
    useBackEndRequests();

  const { data: requestProcessos, refetch: refetchProcesso } = useQuery<IProcessoJuridico[], IErrorWithSentryId>({
    queryKey: ['getListaProcessoJuridico', declaracaoId],
    queryFn: () => getListaProcessoJuridico(declaracaoId),
    refetchOnWindowFocus: false,
    enabled: !!declaracaoId,
  });

  useEffect(() => {
    if (requestProcessos) {
      setListaProcesso(requestProcessos);

      if (houveDecisaoJudicial === false && requestProcessos.length > 0) {
        setHouveDecisaoJudicial(true);
      }
    }
  }, [requestProcessos]);

  const eventoAdicionarProcesso = async () => {
    const novoProcesso = listaProcesso.findIndex((processo) => processo.numero === numeroProcesso);

    if (numeroProcesso.length === 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `É necessário fornecer o número do processo judicial`,
      });
    } else if (novoProcesso !== -1) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Já existe um processo judicial com o mesmo número`,
      });
    } else {
      const request = {
        declaracaoId,
        numero: numeroProcesso,
      };

      await postProcessoJuridicio(request)
        .then(() => {
          refetchProcesso();
          setNumeroProcesso('');
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Ocorreu um erro ao adicionar um novo processo judicial',
            ...(err && { complementoSentryId: err }),
          });
        });
    }
  };

  const onKeyDownAdicionarProcesso = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEY_ENTER) {
      eventoAdicionarProcesso();
    } else {
      event.stopPropagation();
    }
  };

  const eventoExcluirProcesso = async (id: number) => {
    await deleteProcessoJuridicoById(id)
      .then(() => {
        refetchProcesso();
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Ocorreu um erro ao excluir um processo judicial',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  const limparProcessos = async () => {
    setIsModalConfirmacaoOpen(false);

    await deleteAllProcessosJuridicos(declaracaoId)
      .then(() => {
        mostrarNotificacao({
          tipo: 'success',
          conteudo: `Todos os processos foram excluídos`,
        });

        refetchProcesso();
        setHouveDecisaoJudicial(false);
        setNumeroProcesso('');
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Ocorreu um erro ao excluir os processos',
          complementoSentryId: err,
        });
      });
  };

  const onChangeHouveDecisaoJudicial = (checked: boolean) => {
    if (houveDecisaoJudicial && listaProcesso.length !== 0) {
      setIsModalConfirmacaoOpen(true);
    } else {
      setHouveDecisaoJudicial(checked);
    }
  };

  const onKeyDownHouveDecisaoJudicial = (event: React.KeyboardEvent<HTMLDivElement>, checked: boolean) => {
    if (event.key === KEY_ENTER) {
      onChangeHouveDecisaoJudicial(checked);
    } else {
      event.stopPropagation();
    }
  };

  const htmlModalConfirmacao = () => {
    return (
      <>
        <Modal title='Deseja realmente continuar?' useScrim modalOpened={isModalConfirmacaoOpen}>
          <Modal.Body className={style.modalContent}>
            Ao desmarcar esssa opção, <strong>TODOS</strong> os processos judiciais previamente adicionados serão{' '}
            <strong>EXCLUÍDOS</strong>.
          </Modal.Body>
          <Modal.Footer justify-content='end'>
            <Button id='modal-btn-cancelar' secondary small m={2} onClick={() => setIsModalConfirmacaoOpen(false)}>
              Cancelar
            </Button>
            <CustomConfirmer id='modal-btn-confirmar' asyncCall={() => limparProcessos()}>
              Confirmar
            </CustomConfirmer>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  const htmlCampoDecisao = () => {
    return (
      <>
        <div className={style.divider}>
          <BrDivider />
        </div>
        <Row>
          <Col size={8} className={style.switchLabel}>
            Houve Decisão Judicial Favorável relativa a outras Deduções na Base de Cálculo da CFEM?
          </Col>
          <Col size={4} className={style.switch}>
            <div
              id='switch-decisao'
              className={`br-switch icon`}
              onClick={() => onChangeHouveDecisaoJudicial(!houveDecisaoJudicial)}
              onKeyDown={(event) => onKeyDownHouveDecisaoJudicial(event, !houveDecisaoJudicial)}
              role='switch'
              aria-checked={houveDecisaoJudicial}
              tabIndex={0}
            >
              <input type='checkbox' name='switch-icon' checked={houveDecisaoJudicial} disabled={true} role='switch' />
              <label>{houveDecisaoJudicial ? 'Sim' : 'Não'}</label>
            </div>
          </Col>
        </Row>
        <div className={style.divider}>
          <BrDivider />
        </div>
      </>
    );
  };

  const htmlFormulario = () => {
    return (
      <div className={style.containerDinamico}>
        <Row>
          <Col size={COL_NUMERO_PROCESSO}>
            <span className={style.label}>Número do Processo Judicial</span>

            <BrInput
              id='input-processo'
              maxLength={MAX_PROCESSO_JURIDICO}
              value={numeroProcesso}
              onChange={(event) => setNumeroProcesso(event.target.value)}
              onKeyDown={(event) => onKeyDownAdicionarProcesso(event)}
            />
          </Col>
          <Col size={COL_ACOES} className={style.button}>
            <BrButton id='btn-inserir' circle icon='fas fa-plus' secondary onClick={() => eventoAdicionarProcesso()} />
          </Col>
        </Row>
      </div>
    );
  };

  const htmlTabelaProcessos = () => {
    return (
      <div className={classNames(style.containerDinamico, style.tabela)}>
        {listaProcesso?.map((processo) => (
          <div key={processo.id ?? -1} className={style.linhaProcesso}>
            <Row className={style.rowTabela}>
              <Col size={COL_NUMERO_PROCESSO}>
                <span className={style.valorProcesso}>{processo.numero}</span>
              </Col>
              <Col size={COL_ACOES}>
                <div className={style.deleteButton}>
                  <BrButton
                    size='small'
                    circle
                    icon='fas fa-trash'
                    onClick={() => eventoExcluirProcesso(processo.id ?? -1)}
                  />
                </div>
              </Col>
            </Row>
            <BrDivider />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={style.titularArrendatarioJudicial}>
      {htmlModalConfirmacao()}

      <div className={style.container}>
        <h4 className={style.title}>Processos Judiciais</h4>

        {htmlCampoDecisao()}

        {houveDecisaoJudicial && (
          <div className={style.formNumeroProcesso}>
            {htmlFormulario()}
            {htmlTabelaProcessos()}
          </div>
        )}
      </div>
    </div>
  );
};

export default TitularArrendatarioJudicial;
